import React, { useRef, useState } from 'react';
import '../advanced-filter-button/AdvancedFilterButton.scss';
import { useOutsideHandler } from '../../utils/outside-click-handler';
import { useTranslation } from 'react-i18next';
import ionFilter from '../../assets/icons/filter.svg';
import { ASSETS_URL } from '../../constants';

export interface FilterSection {
  key: string;
  titleKey: string;
  options: { id: string; name: string }[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  searchable?: boolean;
}

interface Props {
  sections: FilterSection[];
  activeCount: number;
  label?: string;
}

const CandidateAdvancedFilter = ({ sections, activeCount, label }: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const wrapperRef = useRef<HTMLInputElement>(null);
  useOutsideHandler(wrapperRef, setIsOpen);

  function getFiltered(section: FilterSection): { id: string; name: string }[] {
    const term = (searchTerms[section.key] ?? '').toLowerCase();
    if (!term) return section.options;
    return section.options.filter((o) => o.name.toLowerCase().includes(term));
  }

  return (
    <div className='adv-filter' ref={wrapperRef}>
      <button
        className={`adv-filter__button ${activeCount > 0 ? 'adv-filter__button--active' : ''}`}
        onClick={() => setIsOpen((v) => !v)}
      >
        <img src={ASSETS_URL + ionFilter} alt='filter' />
        <span>{label ?? t('candidatesTablet.filters.advFilters')}</span>
        {activeCount > 0 && (
          <span className='adv-filter__badge'>{activeCount}</span>
        )}
      </button>

      {isOpen && (
        <div className='adv-filter__panel'>
          {sections.map((section, idx) => (
            <React.Fragment key={section.key}>
              {idx > 0 && <div className='adv-filter__divider' />}
              <div className='adv-filter__section'>
                <p className='adv-filter__section-title'>
                  {t(section.titleKey)}
                </p>
                {section.searchable && (
                  <input
                    type='text'
                    className='form-control form-control-sm mb-2'
                    placeholder={t('candidatesTablet.filters.advSearch')}
                    value={searchTerms[section.key] ?? ''}
                    onChange={(e) =>
                      setSearchTerms((prev) => ({
                        ...prev,
                        [section.key]: e.target.value,
                      }))
                    }
                  />
                )}
                {getFiltered(section).map((opt) => (
                  <label key={opt.id} className='adv-filter__option'>
                    <input
                      type='checkbox'
                      checked={section.selectedIds.includes(opt.id)}
                      onChange={() => section.onToggle(opt.id)}
                    />
                    <span>{opt.name}</span>
                  </label>
                ))}
                {getFiltered(section).length === 0 && (
                  <span className='adv-filter__section-title' style={{ fontWeight: 400, textTransform: 'none' }}>
                    —
                  </span>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default CandidateAdvancedFilter;
